import { Canvas } from '@react-three/fiber';
import Experience from './components/Experience';
import { useGLTF, Text, Center } from '@react-three/drei';
import { Suspense, useState } from 'react';
import { cn } from './utils/utils';
import Iconify from './components/Iconify';
import Header from './components/Header';
import ModelNavigation from './components/ModelNavigation';
import LanguageChanger from './components/LanguageChanger';
import EnhancedExperience from './components/EnhancedExperience';
import RacketGallery from './components/RacketGallery';
import { useTranslation } from 'react-i18next';

const MODELS = [
  'https://ams1.vultrobjects.com/courtwin-staging/models/BlackRacketV6.glb',
  'https://ams1.vultrobjects.com/courtwin-staging/models/WhiteRacketV6.glb',
];

export default function App() {
  const {
    t,
    i18n: { language, changeLanguage },
  } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(language);

  const handleChangeLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'pt' : 'en';
    setCurrentLanguage(newLanguage);
    changeLanguage(newLanguage);
  };

  const [isModelOpen, setIsModelOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [activeModel, setActiveModel] = useState(1);

  const handleModelOpen = () => {
    setIsModelOpen(true);
  };

  const handleModelClose = () => {
    setIsModelOpen(false);
    setShowDetails(false);
  };

  const handleModelChange = (modelNumber) => {
    setActiveModel(modelNumber);
  };

  return (
    <div className="relative w-full h-screen bg-black max-h-dvh font-inter">
      {/* Background text layer */}
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none select-none">
        <h1
          className={cn(
            'text-[200px]/[180px] md:text-[147px]/[120px] lg:text-[327px]/[290px] font-normal text-white opacity-100 uppercase text-center relative transition-opacity duration-300 font-kenyan-coffee tracking-[-0.02em] translate-y-[-80px] md:translate-y-[-16px] lg:translate-y-0',
            isModelOpen ? 'opacity-0' : 'opacity-100',
          )}
        >
          Cristiano <br /> Ronaldo
        </h1>
      </div>

      {/* 3D Canvas layer */}
      <div className="absolute inset-0 z-10">
        <Canvas
          className="w-full h-full"
          camera={{
            fov: 100,
            near: 0.1,
            far: 2000,
            position: [0, 0, isModelOpen ? 2 : 1.2],
          }}
          gl={{
            alpha: true,
            antialias: true,
          }}
        >
          <Suspense fallback={null}>
            {/* <Suspense fallback={<LoadingScreen />}> */}
            <EnhancedExperience
              modelUrl={MODELS[activeModel - 1]}
              isModelOpen={isModelOpen}
              showDetails={showDetails}
              language={currentLanguage}
            />
            {/* </Suspense> */}
          </Suspense>
        </Canvas>
      </div>

      <Header />

      {/* Front Layer - Transparent text with white border */}
      <div
        className={cn(
          'absolute inset-0 z-20 flex items-center justify-center',
          isModelOpen ? 'pointer-events-none' : '',
        )}
      >
        <div
          className={cn(
            'relative',
            isModelOpen
              ? 'pointer-events-none opacity-0'
              : 'pointer-events-auto opacity-100',
          )}
        >
          <h1
            className={cn(
              'text-[200px]/[180px] md:text-[147px]/[120px] lg:text-[327px]/[290px] font-normal text-transparent uppercase pointer-events-none select-none transition-all duration-300 font-kenyan-coffee text-center tracking-[-0.02em] translate-y-[-80px] md:translate-y-[-16px] lg:translate-y-0',
              isModelOpen ? 'opacity-0' : 'opacity-100',
            )}
            style={{
              WebkitTextStroke: isModelOpen
                ? '1.2px transparent'
                : `1.2px ${activeModel === 1 ? '#F7C109' : '#F7C109'}`,
              opacity: isModelOpen ? 1 : 0.8,
            }}
          >
            Cristiano <br />
            <span className="invisible">Ronaldo</span>
          </h1>

          {/* <div className="absolute left-1/2 -translate-x-1/2 top-0 max-w-[288px] lg:max-w-[1000px] w-full z-10 flex justify-between">
            <p className="text-[12px]/[15.65px] lg:text-[22.35px]/[31.29px] text-white uppercase font-medium tracking-[0.4em] text-center transition-opacity duration-300 select-none">
              Presenting
            </p>
            <p className="text-[12px]/[15.65px] lg:text-[22.35px]/[31.29px] text-white uppercase font-medium tracking-[0.4em] text-center transition-opacity duration-300 select-none">
              A brand new
            </p>
          </div> */}
        </div>
      </div>

      <div className="absolute z-30 -translate-x-1/2 bottom-16 md:bottom-5 lg:bottom-24 left-1/2 max-w-[331px] md:max-w-[727px] lg:max-w-[715px] w-full pointer-events-none">
        <p
          className={cn(
            'text-[24px]/[23.47px] lg:text-[32.35px]/[31.29px] text-white uppercase font-normal tracking-[0.1em] text-center transition-opacity duration-300 select-none font-square-sans-serif-7',
            isModelOpen ? 'opacity-0' : 'opacity-100',
          )}
        >
          {t('padelRackets')}
        </p>

        <p
          className={cn(
            'text-[14px]/[20px] text-white text-center mt-[10px] lg:mt-[12px] font-normal pointer-events-none transition-opacity duration-300 select-none font-inter',
            isModelOpen ? 'opacity-0' : 'opacity-100',
          )}
        >
          {t('padelRacketsDescription')}
        </p>

        <div className="flex justify-center mt-[24px] lg:mt-[16px]">
          <button
            className={cn(
              'text-white text-[12.57px]/[18.66px] lg:text-[16px]/[24px] font-normal bg-white/20 py-2 px-4 lg:px-[24px] lg:py-[16px]  rounded-full backdrop-blur-[10px] hover:bg-white/30 transition-all duration-300 select-none flex items-center gap-2 lg:gap-2',
              isModelOpen
                ? 'pointer-events-none opacity-0'
                : 'pointer-events-auto opacity-100',
            )}
            onClick={isModelOpen ? handleModelClose : handleModelOpen}
          >
            <Iconify icon="mdi:play" className="size-5 lg:size-6" />
            {t('explore3dModel')}
          </button>
        </div>
      </div>

      <div
        className={cn(
          'flex justify-center mt-[32px] absolute bottom-14 md:bottom-4 z-30 transition-all duration-300 left-1/2 -translate-x-1/2 gap-4',
          isModelOpen
            ? 'pointer-events-auto opacity-100 scale-100'
            : 'pointer-events-none opacity-0 scale-0',
        )}
      >
        <button
          className="text-white text-[12.57px]/[18.66px] lg:text-[16px]/[24px] font-normal bg-white/20 py-2 px-4 md:px-3 md:py-1.5 lg:px-[24px] lg:py-[16px] rounded-full backdrop-blur-[10px] hover:bg-white/30 transition-all duration-300 flex items-center gap-1 select-none"
          onClick={isModelOpen ? handleModelClose : handleModelOpen}
        >
          <Iconify icon="mdi:close" className="size-5" />
          {t('close')}
        </button>

        {isModelOpen && (
          <button
            className="text-white text-[12.57px]/[18.66px] lg:text-[16px]/[24px] font-normal bg-white/20 py-2 px-4 md:px-3 md:py-1.5 lg:px-[24px] lg:py-[16px] rounded-full backdrop-blur-[10px] hover:bg-white/30 transition-all duration-300 select-none items-center gap-2 lg:gap-2 hidden md:flex"
            onClick={() => setShowDetails(!showDetails)}
          >
            <Iconify
              icon={showDetails ? 'mdi:information-off' : 'mdi:information'}
              className="size-5 lg:size-6"
            />
            {showDetails ? t('hideDetails') : t('showDetails')}
          </button>
        )}
      </div>

      {/* Top Layer - Gradient background */}
      <div
        className={cn(
          'absolute z-20 pointer-events-none h-[316px] md:h-[107px] lg:h-[187px] w-full top-0',
          isModelOpen ? 'opacity-0' : 'opacity-100',
        )}
        style={{
          background:
            'linear-gradient(360deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%)',
        }}
      />

      {/* Bottom Layer - Gradient background */}
      <div
        className={cn(
          'absolute z-20 pointer-events-none h-[519px] md:h-[226px] lg:h-[476px] w-full bottom-0',
          isModelOpen ? 'opacity-0' : 'opacity-100',
        )}
        style={{
          background:
            'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 53.83%, #000000 100%)',
        }}
      />

      <div className="absolute z-20 bottom-0 right-0 px-[24px] lg:px-[48px] py-[12px] lg:py-[24px] flex items-center justify-between w-full pointer-events-none">
        <LanguageChanger
          isModelOpen={isModelOpen}
          onChangeLanguage={handleChangeLanguage}
          currentLanguage={currentLanguage}
        />
        <ModelNavigation isModelOpen={isModelOpen} onModelChange={handleModelChange} />
      </div>
    </div>
  );
}
